import React, { Suspense, useEffect } from "react";

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css";

import "./App.css";
import CosmicLoader from "./components/common/CosmicLoader";
import { Layout } from "./components/Layouts/Layout";
import { useAuthContext } from "./context/auth-context";
import { AuthGate } from "./context/AuthGate";
import { WebSocketProvider } from "./context/WebSocketContext";
import { router } from "./router/router";

// DEVELOPMENT-ONLY: visual-parity harness for the G.2c plan-step dock.
// Guarded at the lazy itself, not just the route: webpack skips dynamic
// imports inside DefinePlugin-dead branches, so production builds never
// even EMIT the harness chunk (code-reviewer advisory, PR round 3).
const DevDockHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevDockHarness"))
    : null;

// DEVELOPMENT-ONLY: design harness for the v4 Mission page (pear drawer).
// Same guard-at-the-lazy rule as DevDockHarness so production never emits
// the chunk.
const DevMissionHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevMissionHarness"))
    : null;

// DEVELOPMENT-ONLY: v6 plan board prototype on sample data. Same
// guard-at-the-lazy rule, so production never emits the chunk. Unlike the two
// harnesses above it is NOT a route: App() returns it before AuthGate,
// WebSocketProvider and Layout mount (plan F1), so none of them run for it.
const DevPlanBoardHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevPlanBoardHarness"))
    : null;

// DEVELOPMENT-ONLY: the real Plan step (MissionBoard, PlanStepV6 and its own
// dialog) on an in-page fixture network, for browser checks of the board
// inside the step (batch A, A1). Same guard-at-the-lazy rule and the same
// early return before AuthGate as DevPlanBoardHarness.
const DevPlanStepHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevPlanStepHarness"))
    : null;

// DEVELOPMENT-ONLY: the real Kickoff and the real post-kickoff example on an
// in-page fixture network, for browser checks of S3 (Julie demo, SD-1). Same
// guard-at-the-lazy rule and the same early return before AuthGate as
// DevPlanStepHarness.
const DevJourneyHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevJourneyHarness"))
    : null;

// DEVELOPMENT-ONLY: the real Workspace provider and views on an in-page
// fixture network, for browser checks of S4 linking (Julie demo, plan D-11).
// Same guard-at-the-lazy rule and the same early return before AuthGate as
// DevJourneyHarness.
const DevLinkingHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevLinkingHarness"))
    : null;

// DEVELOPMENT-ONLY: the real Squad board with Keyshawn beside a document on
// an in-page fixture network, for browser checks of S5b (Julie demo, plan
// section 9). Same guard-at-the-lazy rule and the same early return before
// AuthGate as DevLinkingHarness.
const DevSquadKeyshawnHarness =
  process.env.NODE_ENV === "development"
    ? React.lazy(() => import("./pages/DevSquadKeyshawnHarness"))
    : null;

// Loading fallback for lazy-loaded routes
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <CosmicLoader size="sm" showText={false} />
  </div>
);

// Inner component that uses router hooks (must be inside BrowserRouter)
function AppRoutes() {
  const { isCohortUser } = useSelector((state) => state.user);
  const { isAdmin, user } = useAuthContext();
  const [cookies] = useCookies(["token"]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/login");
    }
  }, [location.pathname, navigate]);

  return (
    <WebSocketProvider>
      <div className="App">
        <Layout
          facilitatorUserId={user?.id ?? null}
          facilitatorRoleId={user?.role_id ?? user?.role?.id ?? null}
        >
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              {process.env.NODE_ENV === "development" && (
                <Route path="/dev/dock" element={<DevDockHarness />} />
              )}
              {process.env.NODE_ENV === "development" && (
                <Route path="/dev/mission" element={<DevMissionHarness />} />
              )}
              {router.map((route) => {
                if (cookies.token) {
                  if (route.path === "/login" || route.path === "/register") {
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<Navigate to="/season" replace />}
                      />
                    );
                  }
                  // Allow admin routes for admin users
                  if (route.isAdmin && isAdmin) {
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={route.element}
                      />
                    );
                  }
                  // Redirect non-cohort, non-admin users to season
                  if (route.isAuth && !isCohortUser && !isAdmin) {
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<Navigate to="/season" replace />}
                      />
                    );
                  }
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  );
                } else {
                  if (route.path !== "/login" && route.path !== "/register") {
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<Navigate to="/login" replace />}
                      />
                    );
                  }
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  );
                }
              })}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </Layout>
      </div>
    </WebSocketProvider>
  );
}

// Main App component - wraps everything with required providers
function App() {
  if (
    process.env.NODE_ENV === "development" &&
    DevPlanBoardHarness &&
    window.location.pathname.replace(/\/+$/, "") === "/dev/plan-board"
  ) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageLoadingFallback />}>
          <DevPlanBoardHarness />
        </Suspense>
      </BrowserRouter>
    );
  }
  if (
    process.env.NODE_ENV === "development" &&
    DevPlanStepHarness &&
    window.location.pathname.replace(/\/+$/, "") === "/dev/plan-step"
  ) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageLoadingFallback />}>
          <DevPlanStepHarness />
        </Suspense>
      </BrowserRouter>
    );
  }
  if (
    process.env.NODE_ENV === "development" &&
    DevJourneyHarness &&
    window.location.pathname.replace(/\/+$/, "") === "/dev/journey"
  ) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <DevJourneyHarness />
      </Suspense>
    );
  }
  if (
    process.env.NODE_ENV === "development" &&
    DevLinkingHarness &&
    window.location.pathname.replace(/\/+$/, "") === "/dev/linking"
  ) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <DevLinkingHarness />
      </Suspense>
    );
  }
  if (
    process.env.NODE_ENV === "development" &&
    DevSquadKeyshawnHarness &&
    window.location.pathname.replace(/\/+$/, "") === "/dev/squad-keyshawn"
  ) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <DevSquadKeyshawnHarness />
      </Suspense>
    );
  }
  return (
    <BrowserRouter>
      <AuthGate>
        <AppRoutes />
      </AuthGate>
    </BrowserRouter>
  );
}

export default App;
